import { Component, inject, input, output } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { MATERIAL_CORE } from '@siat-fe/core';
import { filter, map, Observable, switchMap, take, tap } from 'rxjs';
import { DynamicFormHttpService } from '../../dynamic-form/http/dynamicform-http.service';
import {
  CustomModalOptions,
  ModalType,
  SmartModalAction,
  SmartModalService,
} from '../../smart-modal';
import { SnackbarService } from '../../snackbar';
import {
  UploadedFile,
  UploadFileComponent,
} from '../upload-file/upload-file.component';

@Component({
  selector: 'siat-moniser-field',
  templateUrl: './moniser-field.component.html',
  imports: [ReactiveFormsModule, UploadFileComponent, MATERIAL_CORE],
})
export class MoniserFieldComponent {
  /**
   * Input for label
   */
  label = input<string>();

  /**
   * Input for placeholder
   */
  placeholder = input<string>();

  /**
   * Output for hidden save event.
   */
  loadHiddenSave = output();

  /**
   * The parent form group.
   */
  parentForm = input<FormGroup>();

  /**
   * Resolves the base64 preview string for an uploaded file.
   */
  resolvePreview = (
    fileId: string
  ): Observable<string | ArrayBuffer | null> => {
    const dynamicFormHttpService = inject(DynamicFormHttpService);

    return dynamicFormHttpService
      .fetchGetFileById(fileId)
      .pipe(
        map(response =>
          response?.file && response?.type
            ? `data:${response.type};base64,${response.file}`
            : null
        )
      );
  };
  /**
   * Triggers the deletion flow for an uploaded file with a confirmation modal.
   */
  onDeleteFile(uploadedFile: UploadedFile, formControl: AbstractControl): void {
    const dynamicFormHttpService = inject(DynamicFormHttpService);
    const smartModalService = inject(SmartModalService);
    const snackBarService = inject(SnackbarService);
    const control = formControl as FormControl;
    const modalConfig = {
      title: 'Conferma eliminazione',
      message: `Sei sicuro di voler eliminare il file ${uploadedFile.nomeFile}?`,
    } as CustomModalOptions;

    smartModalService
      .openSmartModal(ModalType.CONFIRM, modalConfig)
      .pipe(
        take(1),
        filter(action => action === SmartModalAction.CONFIRMED),
        switchMap(() =>
          dynamicFormHttpService
            .fetchDeleteFile(uploadedFile.fileId, uploadedFile?.fromMoniser)
            .pipe(
              tap(() => {
                control.patchValue(null);
                this.loadHiddenSave.emit();
                snackBarService.showSuccessSnackbar('File eliminato');
              })
            )
        ),

        untilDestroyed(this)
      )
      .subscribe();
  }
}
